// ABOUTME: Form component for creating new email campaigns
// ABOUTME: Handles campaign details, dynamic steps, validation, and submission

import React, { useState, useEffect } from 'react';
import {
  Box as MuiBox,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  FormHelperText,
  Tooltip,
  IconButton,
  ListSubheader,
  Stack,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import axios from 'axios';
import {
  CreateEmailCampaignRequest,
  AllowedTemplatesResponse,
  CampaignExitCondition,
  CheckNameResponse,
  EmailCampaignResponse,
  KBArticleDropdown
} from '../../types/emailCampaign';
import { EXIT_CONDITION_LABELS, EXIT_CONDITION_DESCRIPTIONS, formatTimeForAPI } from '../../utils/campaignUtils';
import { getCurrentAccessToken } from '../../services/api';

interface CreateCampaignFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  editingCampaign?: EmailCampaignResponse | null;
}

interface CampaignStep {
  dayOffset: number;
  sendTime: Date | null;
  templateId: number | '';
  kbArticleId: number | '';
}

interface StepValidationError {
  dayOffset?: string;
  sendTime?: string;
  templateId?: string;
  kbArticleId?: string;
  general?: string;
}

interface FormValidationErrors {
  displayName?: string;
  steps?: StepValidationError[];
}

export default function CreateCampaignForm({ onSuccess, onCancel, editingCampaign }: CreateCampaignFormProps) {
  // Form state
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [exitConditionType, setExitConditionType] = useState<CampaignExitCondition>(CampaignExitCondition.AllStepsCompleted);
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<CampaignStep[]>([
    { dayOffset: 0, sendTime: new Date(0, 0, 0, 9, 0), templateId: '', kbArticleId: '' }
  ]);

  // Track initial state to detect actual changes
  const [initialState, setInitialState] = useState<{
    displayName: string;
    description: string;
    exitConditionType: CampaignExitCondition;
    isActive: boolean;
    steps: CampaignStep[];
  } | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [allowedTemplates, setAllowedTemplates] = useState<AllowedTemplatesResponse | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [availableKBArticles, setAvailableKBArticles] = useState<KBArticleDropdown[]>([]);
  const [loadingKBArticles, setLoadingKBArticles] = useState(true);
  const [nameCheckResult, setNameCheckResult] = useState<CheckNameResponse | null>(null);
  const [checkingName, setCheckingName] = useState(false);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<{
    displayName?: string;
    steps?: Array<{ dayOffset?: string; sendTime?: string; templateId?: string; kbArticleId?: string; general?: string }>;
  }>({});

  useEffect(() => {
    fetchAllowedTemplates();
    fetchAvailableKBArticles();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingCampaign) {
      const name = editingCampaign.displayName;
      const desc = editingCampaign.description || '';
      const exitCond = editingCampaign.exitConditionType;
      const active = editingCampaign.isActive;

      setDisplayName(name);
      setDescription(desc);
      setExitConditionType(exitCond);
      setIsActive(active);

      // Convert steps from API format to form format
      const formSteps: CampaignStep[] = editingCampaign.steps.map(step => {
        // Parse time string "HH:mm:ss" to Date
        const [hours, minutes] = step.sendTime.split(':').map(Number);
        const sendTimeDate = new Date(0, 0, 0, hours, minutes);

        return {
          dayOffset: step.dayOffset,
          sendTime: sendTimeDate,
          templateId: step.templateId ?? '',
          kbArticleId: step.kbArticleId ?? ''
        };
      });
      setSteps(formSteps);

      // Save initial state for change detection
      setInitialState({
        displayName: name,
        description: desc,
        exitConditionType: exitCond,
        isActive: active,
        steps: formSteps
      });
    } else {
      // For new campaigns, set initial state to empty
      setInitialState({
        displayName: '',
        description: '',
        exitConditionType: CampaignExitCondition.AllStepsCompleted,
        isActive: true,
        steps: [{ dayOffset: 0, sendTime: new Date(0, 0, 0, 9, 0), templateId: '', kbArticleId: '' }]
      });
    }
  }, [editingCampaign]);

  // Debounced name checking
  useEffect(() => {
    const timer = setTimeout(() => {
      if (displayName.length >= 3) {
        checkName(displayName);
      } else {
        setNameCheckResult(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [displayName]);

  const checkName = async (name: string) => {
    setCheckingName(true);
    try {
      const token = getCurrentAccessToken();
      const response = await axios.get<CheckNameResponse>(
        `${import.meta.env.VITE_API_URL}/api/admin/email-campaigns/check-name`,
        {
          params: { displayName: name },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setNameCheckResult(response.data);
    } catch (err: unknown) {
      console.error('Error checking name:', err);
      setNameCheckResult(null);
    } finally {
      setCheckingName(false);
    }
  };

  const fetchAllowedTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = getCurrentAccessToken();
      const response = await axios.get<AllowedTemplatesResponse>(
        `${import.meta.env.VITE_API_URL}/api/admin/email-campaigns/allowed-templates`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAllowedTemplates(response.data);
    } catch (err: unknown) {
      console.error('Error fetching allowed templates:', err);
      setError('Failed to load email templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchAvailableKBArticles = async () => {
    setLoadingKBArticles(true);
    try {
      const token = getCurrentAccessToken();
      const response = await axios.get<KBArticleDropdown[]>(
        `${import.meta.env.VITE_API_URL}/api/knowledgebase/admin/available-articles`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAvailableKBArticles(response.data);
    } catch (err: unknown) {
      console.error('Error fetching KB articles:', err);
      setError('Failed to load KB articles');
    } finally {
      setLoadingKBArticles(false);
    }
  };

  const addStep = () => {
    setSteps([...steps, {
      dayOffset: 0,
      sendTime: new Date(0, 0, 0, 9, 0), // 9:00 AM
      templateId: '',
      kbArticleId: ''
    }]);
  };

  const removeStep = (index: number) => {
    if (steps.length > 1) {
      setSteps(steps.filter((_, i) => i !== index));
    }
  };

  const updateStep = <K extends keyof CampaignStep>(index: number, field: K, value: CampaignStep[K]) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const handleCancel = () => {
    // Check if anything has actually changed from initial state
    let hasChanges = false;

    if (initialState) {
      // Compare current state with initial state
      hasChanges =
        displayName !== initialState.displayName ||
        description !== initialState.description ||
        exitConditionType !== initialState.exitConditionType ||
        isActive !== initialState.isActive ||
        steps.length !== initialState.steps.length ||
        steps.some((step, index) => {
          const initialStep = initialState.steps[index];
          if (!initialStep) return true;

          return (
            step.dayOffset !== initialStep.dayOffset ||
            step.sendTime?.getTime() !== initialStep.sendTime?.getTime() ||
            step.templateId !== initialStep.templateId ||
            step.kbArticleId !== initialStep.kbArticleId
          );
        });
    }

    if (hasChanges && !window.confirm('Discard unsaved changes?')) {
      return;
    }

    // Reset form
    setDisplayName('');
    setDescription('');
    setExitConditionType(CampaignExitCondition.AllStepsCompleted);
    setIsActive(true);
    setSteps([{ dayOffset: 0, sendTime: new Date(0, 0, 0, 9, 0), templateId: '', kbArticleId: '' }]);
    setError(null);
    setSuccess(null);
    setValidationErrors({});
    setNameCheckResult(null);
    setInitialState(null);

    onCancel();
  };

  // Group templates by category for dropdown
  const getGroupedTemplates = () => {
    if (!allowedTemplates) return {};

    const grouped: Record<string, typeof allowedTemplates.templates> = {};
    allowedTemplates.templates.forEach(template => {
      if (!grouped[template.category]) {
        grouped[template.category] = [];
      }
      grouped[template.category].push(template);
    });
    return grouped;
  };

  // Group KB articles by category for dropdown
  const getGroupedKBArticles = () => {
    const grouped: Record<string, typeof availableKBArticles> = {};
    availableKBArticles.forEach(article => {
      const category = article.categoryName || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(article);
    });
    return grouped;
  };

  // Get helper text for display name field
  const getNameHelperText = () => {
    if (checkingName) {
      return 'Checking availability...';
    }
    if (nameCheckResult) {
      if (nameCheckResult.available) {
        return `Available! URL slug: ${nameCheckResult.generatedSlug}`;
      } else if (nameCheckResult.suggestedName) {
        return `Name taken. Suggestion: ${nameCheckResult.suggestedName}`;
      } else {
        return 'Name is already in use';
      }
    }
    return `${displayName.length}/150 characters`;
  };

  const validateForm = (): boolean => {
    const errors: FormValidationErrors = {};

    // Validate display name
    if (!displayName.trim()) {
      errors.displayName = 'Campaign name is required';
    } else if (displayName.length > 150) {
      errors.displayName = 'Campaign name must not exceed 150 characters';
    } else if (!editingCampaign) {
      // Only check name availability when creating (not editing)
      if (checkingName) {
        errors.displayName = 'Please wait while we check name availability';
      } else if (!nameCheckResult) {
        errors.displayName = 'Name availability check required';
      } else if (!nameCheckResult.available) {
        errors.displayName = 'Campaign name is not available';
      }
    }

    // Validate steps
    if (steps.length === 0) {
      errors.steps = [{ general: 'At least one step is required' }];
    } else {
      const stepErrors: StepValidationError[] = [];
      const timings = new Set<string>();

      steps.forEach((step, index) => {
        const stepError: StepValidationError = {};

        if (step.dayOffset < 0 || step.dayOffset > 365) {
          stepError.dayOffset = 'Day offset must be between 0 and 365';
        }

        if (!step.sendTime || !(step.sendTime instanceof Date)) {
          stepError.sendTime = 'Valid send time is required';
        }

        // At least one of template or KB article must be selected
        if (!step.templateId && !step.kbArticleId) {
          stepError.general = 'Either an email template or KB article must be selected';
        }

        // Check for duplicate timing
        const timing = `${step.dayOffset}-${formatTimeForAPI(step.sendTime || new Date())}`;
        if (timings.has(timing)) {
          stepError.general = 'Duplicate timing: another step has the same day offset and send time';
        }
        timings.add(timing);

        stepErrors[index] = stepError;
      });

      if (stepErrors.some(e => Object.keys(e).length > 0)) {
        errors.steps = stepErrors;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      setError('Please fix validation errors before submitting');
      return;
    }

    setLoading(true);
    try {
      const token = getCurrentAccessToken();
      const baseUrl = import.meta.env.VITE_API_URL;

      const request: CreateEmailCampaignRequest = {
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        exitConditionType,
        isActive,
        steps: steps.map(step => ({
          dayOffset: step.dayOffset,
          sendTime: formatTimeForAPI(step.sendTime || new Date()),
          templateId: step.templateId || null,
          kbArticleId: step.kbArticleId || null
        }))
      };

      let response: { data: EmailCampaignResponse };

      if (editingCampaign) {
        // Update existing campaign
        response = await axios.put<EmailCampaignResponse>(
          `${baseUrl}/api/admin/email-campaigns/${editingCampaign.id}`,
          request,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        setSuccess(`Campaign "${response.data.displayName}" updated successfully!`);
      } else {
        // Create new campaign
        response = await axios.post<EmailCampaignResponse>(
          `${baseUrl}/api/admin/email-campaigns`,
          request,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        setSuccess(`Campaign "${response.data.displayName}" created successfully!`);
      }

      // Reset form after short delay to show success message
      setTimeout(() => {
        onSuccess(); // Callback to parent to refresh list and close form
      }, 1500);
    } catch (err: unknown) {
      console.error(`Error ${editingCampaign ? 'updating' : 'creating'} campaign:`, err);
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || `Failed to ${editingCampaign ? 'update' : 'create'} campaign`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingTemplates || loadingKBArticles) {
    return (
      <MuiBox display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </MuiBox>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {editingCampaign ? 'Edit Email Campaign' : 'Create Email Campaign'}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <MuiBox component="form" onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ mb: 3 }}>
            {/* Campaign Display Name and Exit Condition Row */}
            <MuiBox sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <MuiBox sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  required
                  label="Campaign Display Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  inputProps={{ maxLength: 150 }}
                  helperText={
                    validationErrors.displayName ||
                    (checkingName ? 'Checking availability...' : getNameHelperText())
                  }
                  error={!!validationErrors.displayName}
                />
              </MuiBox>

              <MuiBox sx={{ flex: 1 }}>
                <FormControl fullWidth required>
                  <InputLabel>Exit Condition</InputLabel>
                  <Select
                    value={exitConditionType}
                    onChange={(e) => setExitConditionType(e.target.value as CampaignExitCondition)}
                    label="Exit Condition"
                  >
                    {Object.entries(EXIT_CONDITION_LABELS).map(([key, label]) => (
                      <MenuItem key={key} value={Number(key)}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {EXIT_CONDITION_DESCRIPTIONS[exitConditionType]}
                  </FormHelperText>
                </FormControl>
              </MuiBox>
            </MuiBox>

            {/* Description */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText="Optional description"
            />

            {/* Active Status */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Campaign is active"
            />
          </Stack>

          {/* Campaign Steps */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Campaign Steps
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Steps will be sent in chronological order based on day offset and time.
          </Typography>

          {steps.map((step, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <MuiBox sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                {/* Day Offset */}
                <MuiBox sx={{ flex: '1 1 30%', minWidth: 0 }}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Day Offset"
                    value={step.dayOffset}
                    onChange={(e) => updateStep(index, 'dayOffset', Number(e.target.value))}
                    inputProps={{ min: 0, max: 365 }}
                    helperText={
                      validationErrors.steps?.[index]?.dayOffset ||
                      'Days after enrollment (0-365)'
                    }
                    error={!!validationErrors.steps?.[index]?.dayOffset}
                  />
                </MuiBox>

                {/* Send Time */}
                <MuiBox sx={{ flex: '1 1 30%', minWidth: 0 }}>
                  <TimePicker
                    label="Send Time *"
                    value={step.sendTime}
                    onChange={(newValue) => updateStep(index, 'sendTime', newValue as Date | null)}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        helperText: validationErrors.steps?.[index]?.sendTime || '24-hour format',
                        error: !!validationErrors.steps?.[index]?.sendTime
                      }
                    }}
                  />
                </MuiBox>

                {/* Email Template */}
                <MuiBox sx={{ flex: '1 1 30%', minWidth: 0 }}>
                  <FormControl fullWidth required error={!!validationErrors.steps?.[index]?.templateId}>
                    <InputLabel>Email Template</InputLabel>
                    <Select
                      value={step.templateId}
                      onChange={(e) => updateStep(index, 'templateId', e.target.value as number)}
                      label="Email Template"
                    >
                      {Object.entries(getGroupedTemplates()).map(([category, templates]) => [
                        <ListSubheader key={category}>{category}</ListSubheader>,
                        ...templates.map(template => (
                          <MenuItem key={template.value} value={template.value}>
                            {template.name}
                          </MenuItem>
                        ))
                      ])}
                    </Select>
                    {validationErrors.steps?.[index]?.templateId && (
                      <FormHelperText error>
                        {validationErrors.steps?.[index]?.templateId}
                      </FormHelperText>
                    )}
                    {!validationErrors.steps?.[index]?.templateId && (
                      <FormHelperText>Select email template</FormHelperText>
                    )}
                  </FormControl>
                </MuiBox>

                {/* KB Article Dropdown */}
                <MuiBox sx={{ flex: '1 1 30%', minWidth: 0 }}>
                  <FormControl
                    fullWidth
                    error={!!validationErrors.steps?.[index]?.kbArticleId}
                  >
                    <InputLabel id={`kb-article-label-${index}`}>
                      Knowledge Base Article (optional)
                    </InputLabel>
                    <Select
                      labelId={`kb-article-label-${index}`}
                      value={step.kbArticleId}
                      label="Knowledge Base Article (optional)"
                      onChange={(e) => updateStep(index, 'kbArticleId', e.target.value)}
                      disabled={loadingKBArticles}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {Object.entries(getGroupedKBArticles()).map(([category, articles]) => [
                        <ListSubheader key={category}>{category}</ListSubheader>,
                        ...articles.map((article) => (
                          <MenuItem key={article.id} value={article.id}>
                            {article.title}
                          </MenuItem>
                        ))
                      ])}
                    </Select>
                    {validationErrors.steps?.[index]?.kbArticleId && (
                      <FormHelperText>{validationErrors.steps[index].kbArticleId}</FormHelperText>
                    )}
                    {!validationErrors.steps?.[index]?.kbArticleId && (
                      <FormHelperText>Optional article attachment</FormHelperText>
                    )}
                  </FormControl>
                </MuiBox>

                {/* Remove Button */}
                <MuiBox sx={{ flex: '0 0 auto', pt: 1 }}>
                  <Tooltip title={steps.length > 1 ? "Remove step" : "At least one step required"}>
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => removeStep(index)}
                        disabled={steps.length === 1}
                        aria-label="Remove step"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </MuiBox>
              </MuiBox>

              {/* General step error (like duplicate timing) */}
              {validationErrors.steps?.[index]?.general && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {validationErrors.steps?.[index]?.general}
                </Alert>
              )}
            </Paper>
          ))}

          {/* Add Step Button */}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addStep}
            sx={{ mb: 3 }}
          >
            Add Step
          </Button>

          <MuiBox sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || loadingTemplates || checkingName}
              onClick={handleSubmit}
            >
              {loading ? <CircularProgress size={24} /> : (editingCampaign ? 'Update Campaign' : 'Create Campaign')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </MuiBox>
        </MuiBox>
      </Paper>
    </LocalizationProvider>
  );
}
